import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PiiUnhideModalComponent } from '../common/pii-unhide-modal/pii-unhide-modal.component';
import { PiiAuditService } from './pii-audit.service';
import { StorageService } from './storage.service';
import { AlertService } from './alert.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PiiFormService {
  // Subject to track form-level unmask state
  private formUnmaskedSubject = new BehaviorSubject<boolean>(false);
  public formUnmasked$ = this.formUnmaskedSubject.asObservable();

  constructor(
    private modalService: NgbModal,
    private piiAuditService: PiiAuditService,
    private storageService: StorageService,
    private alertService: AlertService
  ) {}

  /**
   * Unmask all PII fields in the form
   */
  unmaskAllFields(entityId: string, entityType: string = 'form'): void {
    const modalRef = this.modalService.open(PiiUnhideModalComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'lg'
    });

    modalRef.componentInstance.fieldName = 'All PII Fields in Form';
    modalRef.componentInstance.userId = entityId;
    modalRef.componentInstance.context = 'form';

    modalRef.result.then((result) => {
      if (result) {
        this.logAuditAndUnmaskForm(result, entityId, entityType);
      }
    }).catch(() => {
      // Modal dismissed
    });
  }

  /**
   * Log audit and unmask all form fields
   */
  private logAuditAndUnmaskForm(modalResult: any, entityId: string, entityType: string): void {
    const currentUser = this.storageService.getUserName();
    
    const auditData = {
      userId: entityId,
      fieldName: 'All PII Fields in Form',
      reasonId: modalResult.reasonId,
      reasonDescription: modalResult.reason || 'Unknown reason',
      comment: modalResult.comment,
      accessedBy: currentUser || 'unknown',
      timestamp: new Date().toISOString(),
      context: 'form',
      
      // Entity context
      entityType: entityType,
      entityMobile: '',
      transactionId: '',
      referenceId: entityId
    };

    // Log the audit
    this.piiAuditService.logPiiAccess(auditData).subscribe({
      next: (response) => {
        // Set form-level unmask state
        this.formUnmaskedSubject.next(true);
        this.alertService.successAlertWithMessage('All PII fields unmasked successfully');
        
        // Auto re-mask after 5 minutes for security
        setTimeout(() => {
          this.maskAllFields();
        }, 300000); // 5 minutes
      },
      error: (error) => {
        // Even if audit logging fails, allow unmask for demo purposes
        this.formUnmaskedSubject.next(true);
        this.alertService.successAlertWithMessage('All PII fields unmasked successfully');
        
        // Auto re-mask after 5 minutes for security
        setTimeout(() => {
          this.maskAllFields();
        }, 300000); // 5 minutes
      }
    });
  }

  /**
   * Mask all PII fields in the form
   */
  maskAllFields(): void {
    this.formUnmaskedSubject.next(false);
  }

  /**
   * Get current form unmask state
   */
  isFormUnmasked(): boolean {
    return this.formUnmaskedSubject.value;
  }

  /**
   * Check if form has any PII fields
   */
  formHasPiiFields(): boolean {
    // This could be enhanced to actually check for PII fields in the DOM
    return true; // For now, assume forms have PII fields
  }
} 